import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  CreditCard,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Users,
  Filter,
  Search,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  UserPlus,
  Bell,
  Download,
  X,
} from "lucide-react";
import {
  getFeeStructures,
  createFeeStructure,
  updateFeeStructure,
  deleteFeeStructure,
  getBranchStudentFeeSummaries,
  sendFeeReminders,
} from "../../services/fees";
import {
  FeeStructure,
  CreateFeeStructureRequest,
  UpdateFeeStructureRequest,
  EnhancedFeeFilter as FeeFilter,
} from "../../types/fee";
import { getClasses } from "../../services/classes";
import FeeStructureForm from "../../components/forms/FeeStructureFormImproved";
import FeeAssignmentModal from "../../components/modals/FeeAssignmentModal";
import Pagination from "../../components/common/Pagination";
import { useAuth } from "../../hooks/useAuth";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const AdminFees: React.FC = () => {
  const { user } = useAuth();
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const tableScrollRef2 = useHorizontalDragScroll<HTMLDivElement>();
  const isBranchAdmin = user?.roles.includes("branchadmin");
  const isAdmin = user?.roles.includes("admin");
  const showBranchAdminView = isBranchAdmin || isAdmin; // Show branch admin view for both admin and branchadmin

  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<
    "name" | "balance" | "paid" | "expected"
  >("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [balanceFilter, setBalanceFilter] = useState<
    "all" | "outstanding" | "paid" | "overpaid"
  >("all");
  const [viewType, setViewType] = useState<"monthly" | "total">("monthly");
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [selectedMonth, setSelectedMonth] = useState<number>(1);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<"excel" | "pdf">("excel");
  const [exportScope, setExportScope] = useState<"filtered" | "all">(
    "filtered"
  );
  const [isExporting, setIsExporting] = useState(false);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  // Reset to page 1 when sorting or filtering changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, sortOrder, balanceFilter, pageSize]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // For branch admin - student fee summaries
  const {
    data: studentFeeData,
    isLoading: studentFeeLoading,
    error: studentFeeError,
  } = useQuery({
    queryKey: [
      "branchStudentFees",
      {
        page: currentPage,
        pageSize,
        search: activeSearchTerm,
        sortBy,
        sortOrder,
        balanceFilter,
        viewType,
        periodYear: viewType === "monthly" ? selectedYear : undefined,
        periodMonth: viewType === "monthly" ? selectedMonth : undefined,
      },
    ],
    queryFn: () => {
      return getBranchStudentFeeSummaries({
        page: currentPage,
        limit: pageSize,
        search: activeSearchTerm || undefined,
        sortBy,
        sortOrder,
        balanceFilter,
        viewType,
        periodYear: viewType === "monthly" ? selectedYear : undefined,
        periodMonth: viewType === "monthly" ? selectedMonth : undefined,
      });
    },
    enabled: showBranchAdminView,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // For admin - fee structures (keeping existing logic)
  const [classFilter, setClassFilter] = useState<string>("all");
  const [academicYearFilter, setAcademicYearFilter] = useState<string>("all");
  const [academicTermFilter, setAcademicTermFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");

  // Modal states
  const [isFeeFormOpen, setIsFeeFormOpen] = useState(false);
  const [editingFeeStructure, setEditingFeeStructure] =
    useState<FeeStructure | null>(null);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [selectedFeeForAssignment, setSelectedFeeForAssignment] =
    useState<FeeStructure | null>(null);

  // Build filters object for admin
  const filters: FeeFilter = {
    page: currentPage,
    limit: pageSize,
    search: activeSearchTerm || undefined,
    classId: classFilter !== "all" ? classFilter : undefined,
    academicYear: academicYearFilter !== "all" ? academicYearFilter : undefined,
    academicTerm: academicTermFilter !== "all" ? academicTermFilter : undefined,
    isActive: statusFilter === "all" ? undefined : statusFilter === "active",
  };

  // Queries for admin
  const {
    data: feeStructuresData,
    isLoading: feeStructuresLoading,
    error: feeStructuresError,
  } = useQuery({
    queryKey: ["feeStructures", filters],
    queryFn: () => getFeeStructures(filters),
    enabled: !showBranchAdminView,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch classes for filter dropdown only when needed
  const [shouldFetchClasses, setShouldFetchClasses] = useState(false);
  const { data: classesData } = useQuery({
    queryKey: ["classes"],
    queryFn: () => getClasses({ limit: 100 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: shouldFetchClasses && !isBranchAdmin, // Only fetch when enabled and not branch admin
    refetchOnWindowFocus: false,
  });

  // Calculate statistics from the actual data
  const statistics = React.useMemo(() => {
    if (!feeStructuresData?.data) {
      return {
        totalStructures: 0,
        activeStructures: 0,
        inactiveStructures: 0,
        avgTotalAmount: 0,
      };
    }

    const structures = feeStructuresData.data;
    const activeStructures = structures.filter(
      (structure) => structure.isActive
    ).length;
    const avgAmount =
      structures.length > 0
        ? structures.reduce(
            (sum, structure) => sum + structure.totalAmount,
            0
          ) / structures.length
        : 0;

    return {
      totalStructures: structures.length,
      activeStructures: activeStructures,
      inactiveStructures: structures.length - activeStructures,
      avgTotalAmount: Math.round(avgAmount),
    };
  }, [feeStructuresData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: createFeeStructure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      toast.success("Fee structure created successfully");
      setIsFeeFormOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create fee structure");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateFeeStructureRequest;
    }) => updateFeeStructure(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      toast.success("Fee structure updated successfully");
      setIsFeeFormOpen(false);
      setEditingFeeStructure(null);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update fee structure");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFeeStructure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
      toast.success("Fee structure deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete fee structure");
    },
  });

  // Handlers
  const handleCreateFeeStructure = (
    data: CreateFeeStructureRequest | UpdateFeeStructureRequest
  ) => {
    createMutation.mutate(data as CreateFeeStructureRequest);
  };

  const handleEditFeeStructure = (feeStructure: FeeStructure) => {
    setEditingFeeStructure(feeStructure);
    setIsFeeFormOpen(true);
  };

  const handleUpdateFeeStructure = (
    data: CreateFeeStructureRequest | UpdateFeeStructureRequest
  ) => {
    if (editingFeeStructure) {
      updateMutation.mutate({
        id: editingFeeStructure._id,
        data: data as UpdateFeeStructureRequest,
      });
    }
  };

  const handleDeleteFeeStructure = (id: string) => {
    if (confirm("Are you sure you want to delete this fee structure?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setIsFeeFormOpen(false);
    setEditingFeeStructure(null);
  };

  const handleAssignFee = (feeStructure: FeeStructure) => {
    setSelectedFeeForAssignment(feeStructure);
    setIsAssignmentModalOpen(true);
  };

  const handleCloseAssignmentModal = () => {
    setIsAssignmentModalOpen(false);
    setSelectedFeeForAssignment(null);
  };

  const handleAssignmentSuccess = () => {
    // Refresh data if needed
    queryClient.invalidateQueries({ queryKey: ["feeStructures"] });
  };

  const resetFilters = () => {
    setSearchTerm("");
    setActiveSearchTerm("");
    setClassFilter("all");
    setAcademicYearFilter("all");
    setAcademicTermFilter("all");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  // Remind mutation for branch admin
  const remindMutation = useMutation({
    mutationFn: sendFeeReminders,
    onSuccess: () => {
      toast.success("Fee reminder sent successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send reminder");
    },
  });

  // Bulk remind mutation
  const bulkRemindMutation = useMutation({
    mutationFn: sendFeeReminders,
    onSuccess: () => {
      toast.success("Bulk fee reminders sent successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send bulk reminders");
    },
  });

  const handleRemindStudent = (studentId: string) => {
    remindMutation.mutate([studentId]);
  };

  const handleBulkRemind = () => {
    if (!studentFeeData?.data) return;

    // Get all student IDs with outstanding balances from current view
    const studentsWithBalances = studentFeeData.data
      .filter((item: any) => item.feeSummary.balance > 0)
      .map((item: any) => item.student._id);

    if (studentsWithBalances.length === 0) {
      toast("No students with outstanding balances found in current view", {
        icon: "ℹ️",
      });
      return;
    }

    const confirmed = window.confirm(
      `Send fee reminders to ${studentsWithBalances.length} student(s) with outstanding balances?`
    );

    if (confirmed) {
      bulkRemindMutation.mutate(studentsWithBalances);
    }
  };

  // Export handlers
  const handleExport = async () => {
    setIsExporting(true);
    try {
      let dataToExport: any[] = [];

      if (showBranchAdminView) {
        // Branch admin view - export student fees
        if (exportScope === "all") {
          // Fetch all data without pagination
          const allData = await getBranchStudentFeeSummaries({
            page: 1,
            limit: 10000,
            sortBy,
            sortOrder,
            balanceFilter,
            viewType,
            periodYear: viewType === "monthly" ? selectedYear : undefined,
            periodMonth: viewType === "monthly" ? selectedMonth : undefined,
          });
          dataToExport = allData.data;
        } else {
          // Use current filtered/paginated data
          dataToExport = studentFeeData?.data || [];
        }

        if (exportFormat === "excel") {
          exportStudentFeesToExcel(dataToExport);
        } else {
          exportStudentFeesToPDF(dataToExport);
        }
      } else {
        // Admin view - export fee structures
        if (exportScope === "all") {
          // Fetch all data without pagination
          const allData = await getFeeStructures({
            ...filters,
            limit: 10000,
            page: 1,
          });
          dataToExport = allData.data;
        } else {
          // Use current filtered data
          dataToExport = feeStructuresData?.data || [];
        }

        if (exportFormat === "excel") {
          exportFeeStructuresToExcel(dataToExport);
        } else {
          exportFeeStructuresToPDF(dataToExport);
        }
      }

      toast.success(`Successfully exported to ${exportFormat.toUpperCase()}`);
      setShowExportModal(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const exportStudentFeesToExcel = (data: any[]) => {
    // Dynamically import xlsx
    import("xlsx").then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(
        data.map((item: any) => ({
          "Student Name": `${item.student.userInfo?.firstName} ${item.student.userInfo?.lastName}`,
          "Admission Number":
            item.student.admissionNumber || item.student.studentId,
          "Expected Amount (KES)": item.expected || 0,
          "Amount Paid (KES)": item.paid || 0,
          "Balance (KES)": item.balance || 0,
          Status:
            (item.balance || 0) > 0
              ? "Outstanding"
              : (item.balance || 0) < 0
              ? "Overpaid"
              : "Paid",
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Student Fees");

      const fileName = `student-fees-${
        viewType === "monthly" ? `${selectedYear}-${selectedMonth}` : "total"
      }-${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    });
  };

  const exportStudentFeesToPDF = (data: any[]) => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text("Student Fee Report", 14, 20);

    doc.setFontSize(11);
    doc.text(
      `Report Type: ${
        viewType === "monthly"
          ? `Monthly (${new Date(
              selectedYear,
              selectedMonth - 1
            ).toLocaleDateString("en-US", { month: "long", year: "numeric" })})`
          : "Total Since Registration"
      }`,
      14,
      28
    );
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 34);

    // Calculate totals
    const totalExpected = data.reduce(
      (sum, item) => sum + (item.expected || 0),
      0
    );
    const totalPaid = data.reduce((sum, item) => sum + (item.paid || 0), 0);
    const totalBalance = data.reduce(
      (sum, item) => sum + (item.balance || 0),
      0
    );

    // Add summary
    doc.setFontSize(10);
    doc.text(`Total Expected: KES ${totalExpected.toLocaleString()}`, 14, 42);
    doc.text(`Total Paid: KES ${totalPaid.toLocaleString()}`, 14, 48);
    doc.text(`Total Balance: KES ${totalBalance.toLocaleString()}`, 14, 54);

    // Add table
    autoTable(doc, {
      startY: 60,
      head: [
        [
          "Student Name",
          "Admission No.",
          "Expected (KES)",
          "Paid (KES)",
          "Balance (KES)",
          "Status",
        ],
      ],
      body: data.map((item: any) => [
        `${item.student.userInfo?.firstName} ${item.student.userInfo?.lastName}`,
        item.student.admissionNumber || item.student.studentId,
        (item.expected || 0).toLocaleString(),
        (item.paid || 0).toLocaleString(),
        (item.balance || 0).toLocaleString(),
        (item.balance || 0) > 0
          ? "Outstanding"
          : (item.balance || 0) < 0
          ? "Overpaid"
          : "Paid",
      ]),
      theme: "grid",
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 8 },
    });

    const fileName = `student-fees-${
      viewType === "monthly" ? `${selectedYear}-${selectedMonth}` : "total"
    }-${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  const exportFeeStructuresToExcel = (data: any[]) => {
    import("xlsx").then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(
        data.map((structure: any) => ({
          "Structure Name":
            structure.name || `Fee Structure ${structure._id.slice(-6)}`,
          Class:
            (structure.classId as any)?.name || structure.classId || "No Class",
          "Academic Year": structure.academicYear,
          "Academic Term": structure.academicTerm,
          "Total Amount (KSh)": structure.totalAmount,
          "Due Date": new Date(structure.dueDate).toLocaleDateString(),
          "Components Count": structure.feeComponents.length,
          Status: structure.isActive ? "Active" : "Inactive",
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Structures");

      const fileName = `fee-structures-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(workbook, fileName);
    });
  };

  const exportFeeStructuresToPDF = (data: any[]) => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Fee Structures Report", 14, 20);

    doc.setFontSize(11);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    doc.text(`Total Structures: ${data.length}`, 14, 34);

    autoTable(doc, {
      startY: 40,
      head: [
        [
          "Structure Name",
          "Class",
          "Year/Term",
          "Amount (KSh)",
          "Due Date",
          "Status",
        ],
      ],
      body: data.map((structure: any) => [
        structure.name || `Fee ${structure._id.slice(-6)}`,
        (structure.classId as any)?.name || "No Class",
        `${structure.academicYear} - ${structure.academicTerm}`,
        structure.totalAmount.toLocaleString(),
        new Date(structure.dueDate).toLocaleDateString(),
        structure.isActive ? "Active" : "Inactive",
      ]),
      theme: "grid",
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 8 },
    });

    const fileName = `fee-structures-${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    doc.save(fileName);
  };

  if (showBranchAdminView) {
    // Branch Admin View - Student Fee Summaries
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-3 shadow-lg">
                <DollarSign className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  Student Fee Management
                </h1>
                <p className="text-slate-600 font-medium mt-1">
                  View student fee payments and send reminders
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowExportModal(true)}
                className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Download className="h-5 w-5 text-blue-500" />
                <span className="text-slate-700">Export</span>
              </button>
              <button
                onClick={handleBulkRemind}
                disabled={bulkRemindMutation.isPending}
                className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <Bell className="h-5 w-5 text-emerald-500" />
                <span className="text-slate-700">
                  {bulkRemindMutation.isPending ? "Sending..." : "Remind All"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Summary */}
        {studentFeeData?.data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white/95 backdrop-blur-xl rounded-xl border border-slate-200/60 shadow-lg p-4 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                    Total Expected
                  </p>
                  <p className="text-xl md:text-2xl font-bold text-slate-900 mt-1 truncate">
                    KES{" "}
                    {studentFeeData.data
                      .reduce(
                        (sum: number, item: any) => sum + (item.expected || 0),
                        0
                      )
                      .toLocaleString()}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl p-2.5 shadow-md flex-shrink-0">
                  <DollarSign className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-xl border border-slate-200/60 shadow-lg p-4 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                    Total Paid
                  </p>
                  <p className="text-xl md:text-2xl font-bold text-slate-900 mt-1 truncate">
                    KES{" "}
                    {studentFeeData.data
                      .reduce(
                        (sum: number, item: any) => sum + (item.paid || 0),
                        0
                      )
                      .toLocaleString()}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl p-2.5 shadow-md flex-shrink-0">
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-xl border border-slate-200/60 shadow-lg p-4 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                    Outstanding Balance
                  </p>
                  <p className="text-xl md:text-2xl font-bold text-red-600 mt-1 truncate">
                    KES{" "}
                    {studentFeeData.data
                      .reduce(
                        (sum: number, item: any) => sum + (item.balance || 0),
                        0
                      )
                      .toLocaleString()}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-xl p-2.5 shadow-md flex-shrink-0">
                  <AlertCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search students... (Press Enter)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="w-full pl-10 pr-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:outline-none transition-all"
                />
              </div>
            </div>

            {/* View Type Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewType("monthly")}
                className={`px-4 py-2 rounded-xl font-bold transition-all ${
                  viewType === "monthly"
                    ? "bg-blue-500 text-white shadow-lg"
                    : "bg-white border-2 border-slate-200 text-slate-700 hover:shadow-md"
                }`}
              >
                Monthly View
              </button>
              <button
                onClick={() => setViewType("total")}
                className={`px-4 py-2 rounded-xl font-bold transition-all ${
                  viewType === "total"
                    ? "bg-blue-500 text-white shadow-lg"
                    : "bg-white border-2 border-slate-200 text-slate-700 hover:shadow-md"
                }`}
              >
                Total View
              </button>

              {viewType === "monthly" && (
                <>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="px-4 py-2 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
                  >
                    <option value={1}>January</option>
                    <option value={2}>February</option>
                    <option value={3}>March</option>
                    <option value={4}>April</option>
                    <option value={5}>May</option>
                    <option value={6}>June</option>
                    <option value={7}>July</option>
                    <option value={8}>August</option>
                    <option value={9}>September</option>
                    <option value={10}>October</option>
                    <option value={11}>November</option>
                    <option value={12}>December</option>
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="px-4 py-2 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
                  >
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                    <option value={2027}>2027</option>
                    <option value={2028}>2028</option>
                  </select>
                </>
              )}

              <span className="text-sm font-medium text-slate-600 ml-2">
                {viewType === "monthly"
                  ? `Showing fees for ${new Date(
                      selectedYear,
                      selectedMonth - 1
                    ).toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}`
                  : "Showing total fees since registration"}
              </span>
            </div>

            {/* Other Filters */}
            <div className="flex gap-2 flex-wrap">
              <select
                value={balanceFilter}
                onChange={(e) =>
                  setBalanceFilter(
                    e.target.value as
                      | "all"
                      | "outstanding"
                      | "paid"
                      | "overpaid"
                  )
                }
                className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
              >
                <option value="all">All Students</option>
                <option value="outstanding">Outstanding Balance</option>
                <option value="paid">Fully Paid</option>
                <option value="overpaid">Overpaid</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(
                    e.target.value as "name" | "balance" | "paid" | "expected"
                  )
                }
                className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
              >
                <option value="name">Sort by Name</option>
                <option value="balance">Sort by Balance</option>
                <option value="paid">Sort by Amount Paid</option>
                <option value="expected">Sort by Expected Amount</option>
              </select>
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>
        </div>

        {/* Student Fee Table */}
        {studentFeeLoading ? (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
              <p className="text-slate-600 font-medium">
                Loading student fees...
              </p>
            </div>
          </div>
        ) : studentFeeError ? (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
            <div className="text-center">
              <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 w-fit mx-auto mb-4">
                <XCircle className="h-12 w-12 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                Error Loading Student Fees
              </h3>
              <p className="text-slate-600 font-medium mt-2">
                Please try refreshing the page.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
            <div ref={tableScrollRef} className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-blue-500" />
                        Student
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-purple-500" />
                        Expected Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                        Amount Paid
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-500" />
                        Balance
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-emerald-500" />
                        Actions
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {studentFeeData?.data?.map((item: any) => (
                    <tr
                      key={item.student._id}
                      className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            {item.student.photoUrl ||
                            item.student.userInfo?.profileDetails
                              ?.profilePicture ? (
                              <img
                                src={
                                  item.student.photoUrl ||
                                  item.student.userInfo?.profileDetails
                                    ?.profilePicture
                                }
                                alt={`${item.student.userInfo?.firstName} ${item.student.userInfo?.lastName}`}
                                className="h-10 w-10 rounded-xl object-cover shadow-md border-2 border-slate-200"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display =
                                    "none";
                                  (
                                    e.target as HTMLImageElement
                                  ).nextElementSibling?.classList.remove(
                                    "hidden"
                                  );
                                }}
                              />
                            ) : null}
                            <div
                              className={`h-10 w-10 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-md ${
                                item.student.photoUrl ||
                                item.student.userInfo?.profileDetails
                                  ?.profilePicture
                                  ? "hidden"
                                  : ""
                              }`}
                            >
                              <span className="text-sm font-bold text-white">
                                {item.student.userInfo?.firstName?.charAt(0) ||
                                  ""}
                                {item.student.userInfo?.lastName?.charAt(0) ||
                                  ""}
                              </span>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-900">
                              {item.student.userInfo?.firstName}{" "}
                              {item.student.userInfo?.lastName}
                            </div>
                            <div className="text-sm text-slate-600 font-medium">
                              {item.student.admissionNumber ||
                                item.student.studentId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-slate-900">
                        <div className="flex items-center gap-2">
                          <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-lg p-1.5">
                            <DollarSign className="h-3.5 w-3.5 text-white" />
                          </div>
                          KES {(item.expected || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-slate-900">
                        <div className="flex items-center gap-2">
                          <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-lg p-1.5">
                            <CheckCircle className="h-3.5 w-3.5 text-white" />
                          </div>
                          KES {(item.paid || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-sm font-bold flex items-center gap-2 ${
                            (item.balance || 0) > 0
                              ? "text-red-600"
                              : "text-emerald-600"
                          }`}
                        >
                          <div
                            className={`rounded-lg p-1.5 ${
                              (item.balance || 0) > 0
                                ? "bg-gradient-to-br from-red-300 to-red-400"
                                : "bg-gradient-to-br from-emerald-300 to-green-400"
                            }`}
                          >
                            {(item.balance || 0) > 0 ? (
                              <AlertCircle className="h-3.5 w-3.5 text-white" />
                            ) : (
                              <CheckCircle className="h-3.5 w-3.5 text-white" />
                            )}
                          </div>
                          KES {Math.abs(item.balance || 0).toLocaleString()}
                          {(item.balance || 0) > 0 ? " (Owed)" : " (Credit)"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleRemindStudent(item.student._id)}
                          disabled={remindMutation.isPending}
                          className="bg-white border-2 border-slate-200 px-4 py-2 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                          <Bell className="h-4 w-4 text-emerald-500" />
                          <span className="text-slate-700">
                            {remindMutation.isPending ? "Sending..." : "Remind"}
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {studentFeeData?.pagination && (
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 border-t border-slate-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={studentFeeData.pagination.totalPages}
                  onPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  className="justify-center"
                />
              </div>
            )}
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={() => setShowExportModal(false)}
              ></div>

              <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
                <div className="bg-white px-6 pt-6 pb-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2.5 shadow-lg">
                        <Download className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">
                          Export Data
                        </h3>
                        <p className="text-sm text-slate-600 font-medium">
                          Choose format and scope
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowExportModal(false)}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Export Format */}
                    <div>
                      <label className="block text-sm font-bold text-slate-900 mb-2">
                        Export Format
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setExportFormat("excel")}
                          className={`px-4 py-3 rounded-xl font-bold transition-all border-2 ${
                            exportFormat === "excel"
                              ? "bg-gradient-to-br from-emerald-300 to-green-400 border-emerald-400 text-white shadow-lg"
                              : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                          }`}
                        >
                          <FileText className="h-5 w-5 mx-auto mb-1" />
                          Excel
                        </button>
                        <button
                          onClick={() => setExportFormat("pdf")}
                          className={`px-4 py-3 rounded-xl font-bold transition-all border-2 ${
                            exportFormat === "pdf"
                              ? "bg-gradient-to-br from-red-300 to-red-400 border-red-400 text-white shadow-lg"
                              : "bg-white border-slate-200 text-slate-700 hover:border-red-300"
                          }`}
                        >
                          <FileText className="h-5 w-5 mx-auto mb-1" />
                          PDF
                        </button>
                      </div>
                    </div>

                    {/* Export Scope */}
                    <div>
                      <label className="block text-sm font-bold text-slate-900 mb-2">
                        Export Scope
                      </label>
                      <div className="space-y-2">
                        <button
                          onClick={() => setExportScope("filtered")}
                          className={`w-full px-4 py-3 rounded-xl font-bold transition-all border-2 text-left ${
                            exportScope === "filtered"
                              ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 text-slate-900"
                              : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-bold">Current View</div>
                              <div className="text-xs opacity-75">
                                Export filtered/paginated data
                              </div>
                            </div>
                            <Filter className="h-5 w-5" />
                          </div>
                        </button>
                        <button
                          onClick={() => setExportScope("all")}
                          className={`w-full px-4 py-3 rounded-xl font-bold transition-all border-2 text-left ${
                            exportScope === "all"
                              ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 text-slate-900"
                              : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-bold">All Data</div>
                              <div className="text-xs opacity-75">
                                Export complete dataset
                              </div>
                            </div>
                            <FileText className="h-5 w-5" />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 flex gap-3 border-t border-slate-200">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="flex-1 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-blue-600 rounded-xl font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {isExporting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Export
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Admin View - Fee Structures (existing functionality)
  if (feeStructuresError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <XCircle className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              Error Loading Fee Structures
            </h3>
            <p className="text-slate-600 font-medium mt-2">
              Please try refreshing the page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-3 shadow-lg">
              <CreditCard className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Fee Management
              </h1>
              <p className="text-slate-600 font-medium mt-1">
                Manage fee structures, payments, and financial records
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowExportModal(true)}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Download className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Export</span>
            </button>
            <button
              onClick={() => setIsFeeFormOpen(true)}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Plus className="h-5 w-5 text-emerald-500" />
              <span className="text-slate-700">Create Fee Structure</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {feeStructuresLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-slate-200 rounded w-20"></div>
                  <div className="h-8 bg-slate-200 rounded w-12"></div>
                </div>
                <div className="w-12 h-12 bg-slate-200 rounded-2xl"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">
                  Total Structures
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {statistics.totalStructures}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
                <FileText className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">
                  Active Structures
                </p>
                <p className="text-3xl font-bold text-slate-900 mt-2">
                  {statistics.activeStructures}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-4 shadow-lg">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative min-w-0 flex-1">
              <input
                type="text"
                placeholder="Search fee structures..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 w-full bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:outline-none transition-all"
              />
            </div>

            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              onFocus={() => setShouldFetchClasses(true)} // Fetch classes when dropdown is opened
              className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
            >
              <option value="all">All Classes</option>
              {classesData?.classes?.map((classItem) => (
                <option key={classItem._id} value={classItem._id}>
                  {classItem.name} ({classItem.grade})
                </option>
              ))}
            </select>

            <select
              value={academicYearFilter}
              onChange={(e) => setAcademicYearFilter(e.target.value)}
              className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
            >
              <option value="all">All Years</option>
              <option value="2024-2025">2024-2025</option>
              <option value="2023-2024">2023-2024</option>
              <option value="2022-2023">2022-2023</option>
            </select>

            <select
              value={academicTermFilter}
              onChange={(e) => setAcademicTermFilter(e.target.value)}
              className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
            >
              <option value="all">All Terms</option>
              <option value="Term 1">Term 1</option>
              <option value="Term 2">Term 2</option>
              <option value="Term 3">Term 3</option>
              <option value="Semester 1">Semester 1</option>
              <option value="Semester 2">Semester 2</option>
              <option value="Annual">Annual</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "all" | "active" | "inactive")
              }
              className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <button
            onClick={resetFilters}
            className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
          >
            <Filter className="h-5 w-5 text-slate-500" />
            <span className="text-slate-700">Reset Filters</span>
          </button>
        </div>
      </div>

      {/* Fee Structures Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        {feeStructuresLoading ? (
          <div className="p-12 text-center">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
              <p className="text-slate-600 font-medium">
                Loading fee structures...
              </p>
            </div>
          </div>
        ) : feeStructuresData?.data?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <FileText className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No Fee Structures Found
            </h3>
            <p className="text-slate-600 font-medium mb-6">
              Get started by creating your first fee structure.
            </p>
            <button
              onClick={() => setIsFeeFormOpen(true)}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <Plus className="h-5 w-5 text-emerald-500" />
              <span className="text-slate-700">Create Fee Structure</span>
            </button>
          </div>
        ) : (
          <div ref={tableScrollRef2} className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      Structure Details
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Class & Term
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-emerald-500" />
                      Amount
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-500" />
                      Due Date
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Status
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {feeStructuresData?.data?.map((structure) => (
                  <tr
                    key={structure._id}
                    className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-bold text-slate-900">
                          {structure.name ||
                            `Fee Structure ${structure._id.slice(-6)}`}
                        </div>
                        <div className="text-sm text-slate-600 font-medium">
                          {structure.feeComponents.length} components
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-slate-900">
                        {(structure.classId as any)?.name ||
                          structure.classId ||
                          "No Class Assigned"}
                      </div>
                      <div className="text-sm text-slate-600 font-medium">
                        {structure.academicYear} - {structure.academicTerm}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-slate-900">
                      <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-lg p-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-white" />
                        </div>
                        KSh {structure.totalAmount.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-600">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-500" />
                        {new Date(structure.dueDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold text-white shadow-sm ${
                          structure.isActive
                            ? "bg-gradient-to-br from-emerald-300 to-green-400"
                            : "bg-gradient-to-br from-red-300 to-red-400"
                        }`}
                      >
                        {structure.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleAssignFee(structure)}
                          className="text-emerald-500 hover:bg-emerald-50 p-2 rounded-xl transition-all"
                          title="Assign to students"
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditFeeStructure(structure)}
                          className="text-amber-500 hover:bg-amber-50 p-2 rounded-xl transition-all"
                          title="Edit fee structure"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteFeeStructure(structure._id)
                          }
                          className="text-red-500 hover:bg-red-50 p-2 rounded-xl transition-all"
                          title="Delete fee structure"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {feeStructuresData && feeStructuresData.pagination && (
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 border-t border-slate-200">
            <Pagination
              currentPage={currentPage}
              totalPages={feeStructuresData.pagination.totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>

      {/* Fee Structure Form Modal */}
      {isFeeFormOpen && (
        <FeeStructureForm
          feeStructure={editingFeeStructure}
          onSubmit={
            editingFeeStructure
              ? handleUpdateFeeStructure
              : handleCreateFeeStructure
          }
          onCancel={handleCloseModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Fee Assignment Modal */}
      {isAssignmentModalOpen && selectedFeeForAssignment && (
        <FeeAssignmentModal
          feeStructure={selectedFeeForAssignment}
          onClose={handleCloseAssignmentModal}
          onSuccess={handleAssignmentSuccess}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowExportModal(false)}
            ></div>

            <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-6 pt-6 pb-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2.5 shadow-lg">
                      <Download className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Export Data
                      </h3>
                      <p className="text-sm text-slate-600 font-medium">
                        Choose format and scope
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Export Format */}
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Export Format
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setExportFormat("excel")}
                        className={`px-4 py-3 rounded-xl font-bold transition-all border-2 ${
                          exportFormat === "excel"
                            ? "bg-gradient-to-br from-emerald-300 to-green-400 border-emerald-400 text-white shadow-lg"
                            : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                        }`}
                      >
                        <FileText className="h-5 w-5 mx-auto mb-1" />
                        Excel
                      </button>
                      <button
                        onClick={() => setExportFormat("pdf")}
                        className={`px-4 py-3 rounded-xl font-bold transition-all border-2 ${
                          exportFormat === "pdf"
                            ? "bg-gradient-to-br from-red-300 to-red-400 border-red-400 text-white shadow-lg"
                            : "bg-white border-slate-200 text-slate-700 hover:border-red-300"
                        }`}
                      >
                        <FileText className="h-5 w-5 mx-auto mb-1" />
                        PDF
                      </button>
                    </div>
                  </div>

                  {/* Export Scope */}
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Export Scope
                    </label>
                    <div className="space-y-2">
                      <button
                        onClick={() => setExportScope("filtered")}
                        className={`w-full px-4 py-3 rounded-xl font-bold transition-all border-2 text-left ${
                          exportScope === "filtered"
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 text-slate-900"
                            : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold">Current View</div>
                            <div className="text-xs text-slate-600 font-medium">
                              Export filtered/paginated data
                            </div>
                          </div>
                          <Filter className="h-5 w-5" />
                        </div>
                      </button>
                      <button
                        onClick={() => setExportScope("all")}
                        className={`w-full px-4 py-3 rounded-xl font-bold transition-all border-2 text-left ${
                          exportScope === "all"
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 text-slate-900"
                            : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-bold">All Data</div>
                            <div className="text-xs text-slate-600 font-medium">
                              Export complete dataset
                            </div>
                          </div>
                          <FileText className="h-5 w-5" />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 flex gap-3 border-t border-slate-200">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-blue-600 rounded-xl font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Export
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFees;
